import React, { useEffect, useRef } from 'react';
import HomeHeader from '@/components/HomeHeader';
import Footer from '@/components/homepage/Footer';
import { usePageMeta } from '@/hooks/usePageMeta';
import TableOfContents from '@/components/resources/TableOfContents';
import EmailCapture from '@/components/resources/EmailCapture';

const TITLE = 'Year-End Giving: How to Remind Donors Before December 31 (Without Sounding Pushy)';
const DESCRIPTION =
    'A practical timeline and message templates for reminding donors about year-end giving deadlines — when to send each message, what to say, and how to do it without sounding like a fundraising campaign.';

const JSON_LD = {
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@type': 'Article',
            headline: TITLE,
            description: DESCRIPTION,
            image: 'https://devotionfund.com/resources/cover14.jpg',
            datePublished: '2026-07-29',
            dateModified: '2026-07-29',
            author: { '@type': 'Organization', name: 'Devotion Fund', url: 'https://devotionfund.com' },
            publisher: {
                '@type': 'Organization',
                name: 'Devotion Fund',
                logo: { '@type': 'ImageObject', url: 'https://devotionfund.com/logo.png' },
            },
            mainEntityOfPage: {
                '@id': 'https://devotionfund.com/resources/year-end-giving-reminder-for-missionaries',
                '@type': 'WebPage',
            },
        },
        {
            '@type': 'FAQPage',
            mainEntity: [
                {
                    '@type': 'Question',
                    name: 'When should I send my first year-end giving reminder?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: 'Send a first, low-key mention in early-to-mid December — framed as a year-end update rather than an appeal — then a shorter, more direct reminder in the final week before December 31.',
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Is it appropriate to mention tax deductibility in a year-end reminder?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Yes, as a practical note rather than the main pitch — many donors are genuinely making year-end giving decisions for tax reasons, and a brief, accurate mention (with a reminder to check with their own tax advisor) is helpful, not manipulative.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'How many year-end reminders is too many?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: 'Two is usually enough: one general update in early-to-mid December and one short direct reminder in the last few days of the month. A third message right on December 31 is fine if it is brief and framed as a final deadline note, not a new appeal.',
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Should I ask lapsed donors to give again at year-end?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Yes — year-end is one of the few moments a low-pressure re-invitation to a lapsed donor feels natural rather than random, since many people are already reviewing their giving for the year regardless of whether they've given to you recently.",
                    },
                },
            ],
        },
    ],
};

export default function YearEndGivingReminderForMissionaries() {
    usePageMeta(TITLE, DESCRIPTION, { path: '/resources/year-end-giving-reminder-for-missionaries' });

    const articleRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = document.createElement('script');
        el.type = 'application/ld+json';
        el.setAttribute('data-year-end-giving-jsonld', 'true');
        el.textContent = JSON.stringify(JSON_LD);
        document.head.appendChild(el);
        return () => {
            el.remove();
        };
    }, []);

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <HomeHeader />
            <div className="w-full h-64 md:h-80 bg-gray-100 overflow-hidden">
                <img src="/resources/cover14.jpg" alt="A missionary preparing a year-end giving reminder for supporters" className="w-full h-full object-cover" />
            </div>
            <div className="max-w-3xl mx-auto px-4 py-12 w-full">
                <h1 className="text-3xl font-bold mb-6">{TITLE}</h1>
                <TableOfContents targetRef={articleRef} />
                <div className="prose max-w-none" ref={articleRef}>
                    <p>December is the single biggest giving month of the year for most missionaries and nonprofits, and a large share of that giving happens in the final two weeks — often driven by donors making deliberate, tax-motivated decisions before the calendar year closes. Missing this window because a reminder felt awkward to send is one of the more avoidable gaps in a support-raising year. The fix isn't a hard-sell campaign — it's two well-timed, well-worded messages.</p>

                    <h2>Why year-end giving is different from a normal ask</h2>

                    <p>Unlike a mid-year support letter, a year-end reminder isn't introducing a need — it's giving people who already intend to give (to you or to someone) a nudge and a deadline. Many donors are actively reviewing their charitable giving in December for tax-planning reasons, which means a well-timed reminder is often welcomed rather than resented. The goal is to be the specific, easy option they act on, not to manufacture urgency that isn't there.</p>

                    <h2>A two-message timeline that doesn't feel like a campaign</h2>

                    <p><strong>Message one — early-to-mid December.</strong> Frame this as a genuine year-in-review update: what happened this year, what their support made possible, and a brief, natural mention that gifts before December 31 count for this year's giving. This message should feel like it would have been sent even without a giving mention attached.</p>

                    <p><strong>Message two — the last few days of December.</strong> Shorter and more direct: a one- or two-line reminder that the year is ending, a link to give, and a simple thank-you regardless of whether they act. This is the message that actually captures last-minute deciders — don't skip it out of fear of sounding repetitive; it reaches people who meant to give after message one and simply forgot.</p>

                    <h2>How to mention taxes without making it the whole pitch</h2>

                    <p>It's reasonable and helpful to note that gifts made by December 31 may be tax-deductible for the current year, since this is genuinely useful, practical information for donors weighing timing — but see <a href="/resources/is-missionary-support-tax-deductible">is missionary support tax-deductible</a> for the nuance, since deductibility depends on the giving structure, not just the date. Keep the tax mention to a sentence, always suggest donors confirm specifics with their own tax advisor, and keep the emotional core of the message about impact and gratitude, not the deduction.</p>

                    <h2>Use the moment to re-invite lapsed donors</h2>

                    <p>Year-end is one of the few natural moments to reach out to someone who hasn't given in a while without it feeling like a random guilt trip — most people are already thinking about their annual giving regardless of when they last gave to you. A warm, low-pressure version works well here: "As the year wraps up, I wanted to share what this year looked like and let you know your support, at any level, would mean a lot going into next year." No reference to how long it's been or why they stopped.</p>

                    <h2>What to avoid</h2>

                    <p>Skip artificial countdown language ("only 3 days left!") that borrows retail urgency tactics — it reads as out of place for a personal ministry relationship. Avoid sending more than two substantive messages in December; a third reminder is fine only if it's a brief, final note rather than a new appeal. And don't lead with numbers or shortfalls ("we still need $X by Friday") — that framing shifts the message from gratitude and impact to a transaction, which tends to underperform a warmer ask.</p>

                    <h2>After December 31: say thank you, then plan for January</h2>

                    <p>Send a genuine thank-you to everyone who gave in December, separate from any tax receipt your platform generates automatically. Then use January to follow the same donor-retention rhythm you'd use any other month — see <a href="/resources/donor-retention-for-missionaries">donor retention for missionaries</a> — since a December gift is a strong signal of engagement worth building on, not a one-time transaction to file away.</p>

                    <h2>Frequently asked questions</h2>

                    <p><strong>When should I send my first year-end giving reminder?</strong><br />
                    Early-to-mid December, framed as a year-in-review update, not a direct appeal.</p>

                    <p><strong>Is it appropriate to mention tax deductibility in a year-end reminder?</strong><br />
                    Yes, briefly and practically, with a note to confirm details with their own tax advisor.</p>

                    <p><strong>How many year-end reminders is too many?</strong><br />
                    Two substantive messages is the sweet spot; a brief final-day note is fine as a third.</p>

                    <p><strong>Should I ask lapsed donors to give again at year-end?</strong><br />
                    Yes — it's one of the few moments a re-invitation feels natural rather than random.</p>

                    <h2>Next reads on Devotion Fund</h2>
                    <ul>
                        <li><a href="/resources/is-missionary-support-tax-deductible">Is Missionary Support Tax-Deductible? A Clear Guide for Donors and Missionaries (2026)</a></li>
                        <li><a href="/resources/donor-retention-for-missionaries">Donor Retention for Missionaries: How to Stop Supporters From Quietly Lapsing</a></li>
                        <li><a href="/resources/how-to-ask-a-one-time-donor-to-become-a-monthly-supporter">How to Ask a One-Time Donor to Become a Monthly Supporter</a></li>
                    </ul>

                    <h2>About the author</h2>
                    <p>We're the team behind <a href="/">Devotion Fund</a>. We built this from watching how much of a missionary's yearly giving lands in the last two weeks of December — and how often a reminder that would have helped never gets sent.</p>
                    <p><small><em>Last updated: 2026-07-29.</em></small></p>
                </div>
                <div className="mt-8">
                    <EmailCapture page="/resources/year-end-giving-reminder-for-missionaries" />
                </div>
            </div>
            <Footer />
        </div>
    );
}
